import React, { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Loader2, Shield } from 'lucide-react';
import { OtpFormData } from '../../types';
import { useAuthStore } from '../../stores/authStore';

interface OtpFormProps {
  phoneNumber: string;
  onBack: () => void;
}

export const OtpForm: React.FC<OtpFormProps> = ({ phoneNumber, onBack }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isResending, setIsResending] = useState(false);
  const { verifyOtp, requestOtp, isLoading, error, clearError } = useAuthStore();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [resendCooldown, setResendCooldown] = useState(0);

  const {
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<OtpFormData>();

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return; // Prevent multiple characters
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setValue('otpCode', newOtp.join(''));

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = [...otp];
    
    for (let i = 0; i < pastedData.length && i < 6; i++) {
      newOtp[i] = pastedData[i];
    }
    
    setOtp(newOtp);
    setValue('otpCode', newOtp.join(''));
    
    // Focus the next empty input or the last input
    const nextIndex = Math.min(pastedData.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  const onSubmit = async (data: OtpFormData) => {
    clearError();
    
    try {
      await verifyOtp(phoneNumber, data.otpCode);
    } catch (error) {
      // Error is handled by the store
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    
    setIsResending(true);
    try {
      await requestOtp(phoneNumber);
      setResendCooldown(60); // 60 second cooldown
    } catch (error) {
      console.error('Failed to resend OTP:', error);
    } finally {
      setIsResending(false);
    }
  };

  const isOtpComplete = otp.every(digit => digit !== '');

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="card p-8">
        <div className="text-center mb-8">
          <div className="mx-auto w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
            <Shield className="h-6 w-6 text-primary-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Verify Your Identity
          </h1>
          <p className="text-gray-600">
            We've sent a 6-digit code to{' '}
            <span className="font-medium text-gray-900">{phoneNumber}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* OTP Input Fields */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-4 text-center">
              Enter verification code
            </label>
            <div className="flex justify-center space-x-3">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  className={`w-12 h-12 text-center text-lg font-semibold border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    digit ? 'border-primary-500 bg-primary-50' : 'border-gray-300'
                  } ${errors.otpCode ? 'border-red-500 focus:ring-red-500' : ''}`}
                  aria-label={`Digit ${index + 1} of verification code`}
                />
              ))}
            </div>
            {errors.otpCode && (
              <p className="mt-2 text-sm text-red-600 text-center" role="alert">
                {errors.otpCode.message}
              </p>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div 
              className="bg-red-50 border border-red-200 rounded-lg p-4"
              role="alert"
              aria-live="polite"
            >
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading || !isOtpComplete}
            className="btn-primary w-full flex items-center justify-center"
            aria-describedby={isLoading ? 'loading-text' : undefined}
          >
            {isLoading ? (
              <>
                <Loader2 className="animate-spin h-4 w-4 mr-2" />
                <span id="loading-text">Verifying...</span>
              </>
            ) : (
              'Verify Code'
            )}
          </button>

          {/* Resend OTP */}
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-2">
              Didn't receive the code?
            </p>
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={isResending || resendCooldown > 0}
              className="text-sm text-primary-600 hover:text-primary-500 focus:outline-none focus:underline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isResending ? (
                'Sending...'
              ) : resendCooldown > 0 ? (
                `Resend in ${resendCooldown}s`
              ) : (
                'Resend code'
              )}
            </button>
          </div>

          {/* Back Button */}
          <button
            type="button"
            onClick={onBack}
            className="w-full flex items-center justify-center text-sm text-gray-600 hover:text-gray-500 focus:outline-none focus:underline"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to login
          </button>
        </form>
      </div>
    </div>
  );
};
